import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Collapse,
    CircularProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { DirectoryTreeProps, FolderTreeResponse, folderDisplayName } from '../AssetExplorer.types';
import { FolderStateIcons } from './FolderStateIcons';
import { buildFolderBreadcrumbPath } from '../assetExplorerUiState';
import { useAssetExplorerConfig } from '../AssetExplorerConfig';
import {
    isProfessionalLightTheme,
    professionalLightBorderedIconButtonSx,
    professionalLightFolderTreeColors,
    PROFESSIONAL_LIGHT_ACCENT,
} from '../assetExplorerTheme';

interface TreeNodeProps {
    folder: FolderTreeResponse;
    level: number;
    selectedFolderId: string | null;
    expandedFolders: Set<string>;
    onToggleExpand: (folderId: string) => void;
    onFolderSelect: (folder: FolderTreeResponse) => void;
    onFolderCreate: (parentId: string | null) => void;
    onFolderDelete: (folderId: string) => void;
    onFolderProperties?: (folderId: string) => void;
    /** Hide context menu (e.g. destination picker dialog). */
    pickerMode?: boolean;
    disabledFolderIds?: Set<string>;
    /** True when an earlier ancestor still has a later sibling, so its vertical guide continues. */
    guides?: boolean[];
    isLast?: boolean;
}

const TreeNode: React.FC<TreeNodeProps> = ({
    folder,
    level,
    selectedFolderId,
    expandedFolders,
    onToggleExpand,
    onFolderSelect,
    onFolderCreate,
    onFolderDelete,
    onFolderProperties,
    pickerMode = false,
    disabledFolderIds,
    guides = [],
    isLast = false,
}) => {
    const theme = useTheme();
    const guideColor = alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.55 : 0.45);
    const GUIDE = 18;
    const ROW_MID = 18;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const isExpanded = expandedFolders.has(folder._id);
    const isSelected = selectedFolderId === folder._id;
    const hasChildren = folder.children && folder.children.length > 0;
    const rowDisabled = Boolean(disabledFolderIds?.has(folder._id));
    const folderColors = professionalLightFolderTreeColors(theme, isSelected);
    const proLight = isProfessionalLightTheme(theme);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCreateSubfolder = () => {
        handleMenuClose();
        onFolderCreate(folder._id);
    };

    const handleDelete = () => {
        handleMenuClose();
        onFolderDelete(folder._id);
    };

    const handleProperties = () => {
        handleMenuClose();
        onFolderProperties?.(folder._id);
    };

    const verticalGuide = (left: number, top: number, bottom: number | undefined, height: number | undefined) => (
        <Box
            aria-hidden
            sx={{
                position: 'absolute',
                left,
                top,
                bottom,
                width: '1px',
                height,
                pointerEvents: 'none',
                backgroundImage: `repeating-linear-gradient(to bottom, ${guideColor} 0 2px, transparent 2px 5px)`,
            }}
        />
    );

    return (
        <Box sx={{ position: 'relative' }}>
            {guides.map((continues, index) =>
                continues
                    ? verticalGuide(index * GUIDE + GUIDE / 2, 0, 0, undefined)
                    : null,
            )}
            {level > 0 &&
                verticalGuide(
                    (level - 1) * GUIDE + GUIDE / 2,
                    0,
                    isLast ? undefined : 0,
                    isLast ? ROW_MID : undefined,
                )}
            <Box
                onClick={() => {
                    if (!rowDisabled) {
                        onFolderSelect(folder);
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    minHeight: 36,
                    pr: 1,
                    pl: `${level * GUIDE + 4}px`,
                    cursor: rowDisabled ? 'not-allowed' : 'pointer',
                    opacity: rowDisabled ? 0.48 : 1,
                    borderRadius: 1,
                    backgroundColor: proLight
                        ? (folderColors.rowBg ?? 'transparent')
                        : isSelected
                          ? alpha(theme.palette.secondary.main, 0.14)
                          : 'transparent',
                    '&:hover': {
                        backgroundColor: proLight
                            ? (folderColors.rowHoverBg ?? 'rgba(25, 25, 25, 0.05)')
                            : isSelected
                              ? alpha(theme.palette.secondary.main, 0.22)
                              : alpha(theme.palette.action.hover, rowDisabled ? 0.02 : 0.04),
                    },
                    transition: 'background-color 0.15s ease',
                }}
            >
                {level > 0 && (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            left: (level - 1) * GUIDE + GUIDE / 2,
                            top: ROW_MID,
                            width: GUIDE / 2 + 2,
                            height: '1px',
                            pointerEvents: 'none',
                            backgroundImage: `repeating-linear-gradient(to right, ${guideColor} 0 2px, transparent 2px 5px)`,
                        }}
                    />
                )}
                {hasChildren && isExpanded && (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            left: level * GUIDE + GUIDE / 2,
                            top: '50%',
                            bottom: 0,
                            width: '1px',
                            pointerEvents: 'none',
                            backgroundImage: `repeating-linear-gradient(to bottom, ${guideColor} 0 2px, transparent 2px 5px)`,
                        }}
                    />
                )}
                {/* Expand/Collapse Icon */}
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (hasChildren) onToggleExpand(folder._id);
                    }}
                    sx={{
                        p: 0.25,
                        visibility: hasChildren ? 'visible' : 'hidden',
                        mr: 0.5,
                        ...(proLight && { color: PROFESSIONAL_LIGHT_ACCENT }),
                    }}
                >
                    {isExpanded ? (
                        <ExpandMoreIcon fontSize="small" sx={proLight ? { color: PROFESSIONAL_LIGHT_ACCENT } : undefined} />
                    ) : (
                        <ChevronRightIcon fontSize="small" sx={proLight ? { color: PROFESSIONAL_LIGHT_ACCENT } : undefined} />
                    )}
                </IconButton>

                {/* Folder Icon */}
                {isExpanded ? (
                    <FolderOpenIcon
                        sx={{
                            fontSize: 20,
                            color: folderColors.iconColor,
                            mr: 1,
                        }}
                    />
                ) : (
                    <FolderIcon
                        sx={{
                            fontSize: 20,
                            color: folderColors.iconColor,
                            mr: 1,
                        }}
                    />
                )}

                {/* Folder Name */}
                <Typography
                    variant="body2"
                    sx={{
                        flex: 1,
                        fontWeight: isSelected ? 600 : 400,
                        color: folderColors.textColor,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {folderDisplayName(folder)}
                </Typography>
                <Box sx={{ ml: 0.5, mr: 0.25, display: 'flex', flexShrink: 0 }}>
                    <FolderStateIcons folder={folder} size={13} />
                </Box>

                {/* More Options Menu */}
                {!pickerMode && !folder.shared && (
                    <>
                        <IconButton
                            size="small"
                            onClick={handleMenuOpen}
                            sx={{
                                p: 0.25,
                                opacity: 0,
                                '.MuiBox-root:hover > &': {
                                    opacity: 1,
                                },
                            }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem onClick={handleCreateSubfolder}>
                                <ListItemIcon>
                                    <CreateNewFolderIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>New Subfolder</ListItemText>
                            </MenuItem>
                            {onFolderProperties && (
                                <MenuItem onClick={handleProperties}>
                                    <ListItemIcon>
                                        <SettingsOutlinedIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Properties</ListItemText>
                                </MenuItem>
                            )}
                            {!folder.isRoot && (
                                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                                    <ListItemIcon>
                                        <DeleteOutlineIcon fontSize="small" color="error" />
                                    </ListItemIcon>
                                    <ListItemText>Delete</ListItemText>
                                </MenuItem>
                            )}
                        </Menu>
                    </>
                )}
            </Box>

            {/* Children */}
            {hasChildren && (
                <Collapse in={isExpanded}>
                    {folder.children?.map((child, index, siblings) => (
                        <TreeNode
                            key={child._id}
                            folder={child}
                            level={level + 1}
                            guides={level === 0 ? [] : [...guides, !isLast]}
                            isLast={index === siblings.length - 1}
                            selectedFolderId={selectedFolderId}
                            expandedFolders={expandedFolders}
                            onToggleExpand={onToggleExpand}
                            onFolderSelect={onFolderSelect}
                            onFolderCreate={onFolderCreate}
                            onFolderDelete={onFolderDelete}
                            onFolderProperties={onFolderProperties}
                            pickerMode={pickerMode}
                            disabledFolderIds={disabledFolderIds}
                        />
                    ))}
                </Collapse>
            )}
        </Box>
    );
};

export const DirectoryTree: React.FC<DirectoryTreeProps> = ({
    folders,
    selectedFolderId,
    onFolderSelect,
    onFolderCreate,
    onFolderDelete,
    onFolderProperties,
    loading,
    onCollapseSidebar,
    squareTitleBannerTop = false,
    pickerMode = false,
    disabledFolderIds,
}) => {
    const theme = useTheme();
    const proLight = isProfessionalLightTheme(theme);
    const { title: explorerTitle } = useAssetExplorerConfig();
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

    // Expand root, ancestors, and the opened folder so the side tree matches the main pane.
    React.useEffect(() => {
        if (folders.length === 0) return;

        setExpandedFolders((prev) => {
            const next = new Set(prev);
            const rootId = folders[0]?._id;
            if (rootId) next.add(rootId);

            if (selectedFolderId) {
                const path = buildFolderBreadcrumbPath(folders, selectedFolderId);
                path.forEach((folder) => next.add(folder._id));
            }

            if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
                return prev;
            }
            return next;
        });
    }, [folders, selectedFolderId]);

    const handleToggleExpand = (folderId: string) => {
        setExpandedFolders((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(folderId)) {
                newSet.delete(folderId);
            } else {
                newSet.add(folderId);
            }
            return newSet;
        });
    };

    const titleBannerTopRadius = squareTitleBannerTop ? 0 : theme.shape.borderRadius * 2;

    const sidebarTitleBlock = (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 2,
                    backgroundColor: (t) =>
                        t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                    borderTopLeftRadius: titleBannerTopRadius,
                    borderTopRightRadius: titleBannerTopRadius,
                }}
            >
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                        lineHeight: 1.25,
                        textAlign: 'center',
                    }}
                >
                    {explorerTitle}
                </Typography>
            </Box>
            <Divider />
        </>
    );

    if (loading) {
        return (
            <Box sx={{ pb: pickerMode ? 0 : 1 }}>
                {!pickerMode && sidebarTitleBlock}
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            </Box>
        );
    }

    if (folders.length === 0) {
        return (
            <Box sx={{ pb: pickerMode ? 0 : 1 }}>
                {!pickerMode && sidebarTitleBlock}
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        No folders found
                    </Typography>
                </Box>
            </Box>
        );
    }

    const treeNodes = folders.map((folder) => (
        <TreeNode
            key={folder._id}
            folder={folder}
            level={0}
            selectedFolderId={selectedFolderId}
            expandedFolders={expandedFolders}
            onToggleExpand={handleToggleExpand}
            onFolderSelect={onFolderSelect}
            onFolderCreate={onFolderCreate}
            onFolderDelete={onFolderDelete}
            onFolderProperties={onFolderProperties}
            pickerMode={pickerMode}
            disabledFolderIds={disabledFolderIds}
        />
    ));

    if (pickerMode) {
        return <Box sx={{ pb: 0 }}>{treeNodes}</Box>;
    }

    return (
        <Box sx={{ pb: 1 }}>
            {sidebarTitleBlock}
            {/* Content tree toolbar */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: 2,
                    py: 1,
                    mb: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {onCollapseSidebar && (
                        <Tooltip title="Collapse side panel">
                            <IconButton
                                size="small"
                                onClick={onCollapseSidebar}
                                color={proLight ? undefined : 'secondary'}
                                aria-label="Collapse side panel"
                                sx={professionalLightBorderedIconButtonSx(theme)}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="New Folder">
                        <IconButton
                            size="small"
                            onClick={() => onFolderCreate(folders[0]?._id || null)}
                            color={proLight ? undefined : 'secondary'}
                            sx={professionalLightBorderedIconButtonSx(theme)}
                        >
                            <CreateNewFolderIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Folder Tree */}
            {treeNodes}
        </Box>
    );
};

export default DirectoryTree;
